import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CourseSettings } from '@app/evaluation-settings-menu/CourseSettings'
import { ActivityIndicator } from 'react-native'
import theme from '@app/ui/theme'
import {
  FBREF_ADJUSTMENT_UPDATE_QUEUE,
  QueueTask,
  UpdateTask,
} from '@app/evaluation-core/refs'
import { useFirebaseConnect } from 'react-redux-firebase'
import { useEvaluation } from '@common/hooks'
import { State } from '@app/state/types'
import * as S from './EditCourseSettingsModal.styles'
import Spacer from '@app/components/atoms/Spacer'
import Modal, { ModalHeader, ModalScrollContent } from '@app/components/Modal'

type Props = { isOpen: boolean; onClose: () => void }

const EditCourseSettingsModal = ({ isOpen, onClose }: Props) => {
  const dispatch = useDispatch()
  const { currentId } = useEvaluation()
  const [closeWhenFinished, setCloseWhenFinished] = React.useState(false)

  useFirebaseConnect([
    { path: FBREF_ADJUSTMENT_UPDATE_QUEUE(currentId as string) },
  ])

  const loadingCourseSettings = useSelector(({ firebase: { data } }: State) => {
    const updateQueue = data.queues?.updateAdjustments.adjustments as {
      [key: string]: { [key: string]: QueueTask<UpdateTask> }
    }

    if (!updateQueue) return false

    const arr = Object.values(updateQueue).map((item) => {
      if (item === null) {
        return []
      }
      const task = Object.values(item)[0]
      return task
    }) as QueueTask<UpdateTask>[]

    return arr.some((item) => item?.data?.type === 'course')
  })

  const handleOnClose = async () => {
    setCloseWhenFinished(true)
  }

  useEffect(() => {
    if (closeWhenFinished && !loadingCourseSettings) {
      setCloseWhenFinished(false)
      onClose()
    }
  }, [closeWhenFinished, loadingCourseSettings, onClose])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="800px"
      closeOnBackdropClick={!loadingCourseSettings}
      showCloseButton={!loadingCourseSettings}
    >
      <ModalHeader>
        <h2 style={{ margin: 0 }}>Course settings</h2>
      </ModalHeader>
      <ModalScrollContent
        style={{ position: 'relative', padding: '0 16px 16px' }}
      >
        <CourseSettings onClose={handleOnClose} withValidationLabels inModal />
        {loadingCourseSettings && (
          <S.LoadingOverlay>
            <ActivityIndicator color={theme.colors.secondary2} size={36} />
            <Spacer height={4} />
            <S.LoadingText>Recalculating</S.LoadingText>
          </S.LoadingOverlay>
        )}
      </ModalScrollContent>
    </Modal>
  )
}

export default EditCourseSettingsModal
